import {
  useAccountTitleOptions,
  useAccountingTypes,
  useMinistries,
} from '@/features/master-table/hooks';
import type {
  AccountingTypesModel,
  MinistriesModel,
} from '@/features/master-table/models';
import { usePolicies } from '@/features/master-table/policy/_hooks/policy';
import type { PoliciesWithOrganizationNameModel } from '@/features/master-table/policy/_models/policy';
import { getMinistryName } from '@/features/mold/utils/formatter/rs-ledger';
import { useProjectHasRelatedOrganizations } from '@/features/project/_hooks/organization';
import { useProject } from '@/features/project/_hooks/project';
import type {
  ConsecutiveFiscalYearsModel,
  ConsecutiveRequestAmountsModel,
} from '@/features/project/_models/consecutive';
import { useFilteredUsers } from '@/features/tenant-manager/user/_hooks/user';
import { ITEM_SPLITTER } from '@/utils';
import { uniq } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import type z from 'zod';
import type { IndicatorsModel } from '../../../_models/activity';
import type {
  ConsecutiveBudgetsModel,
  ConsecutiveExecutionsModel,
} from '../../../_models/consecutive';
import type { PaymentsModel } from '../../../_models/payment';
import type { PaymentGraphModel } from '../../../_models/payment-graph';
import type { PaymentGroupsModel } from '../../../_models/payment-group';
import type { ProjectModel } from '../../../_models/project';
import type { RelatedMaterialsModel } from '../../../_models/related-material';
import type { SegmentExpenditureAndExecutionsModel } from '../../../_models/segment-expenditure-and-execution';
import {
  useActivityTreeOptions,
  useNestedConsecutiveIndicators,
} from '../../activities/_hooks/activity';
import {
  useConsecutiveBudgets,
  useConsecutiveExecutions,
  useConsecutiveFiscalYears,
  useConsecutiveRequestAmounts,
} from '../../activities/_hooks/consecutive';
import { useFundBusinesses } from '../../fund-bussiness/_hooks/fund-business';
import type { FundBusinessesModel } from '../../fund-bussiness/_model/fund-business';
import { useSegmentExpenditureAndExecution } from '../../income-and-expenditure/_hooks/segment-expenditure-and-execution';
import { useContractorPayments } from '../../payment/_hooks/payment';
import { usePaymentGraph } from '../../payment/_hooks/payment-graph';
import { usePaymentGroups } from '../../payment/_hooks/payment-group';
import { useRelatedMaterials } from '../../related-material/_hooks/related-material';

type SheetType = 'RS' | 'SS' | 'KS';

type AdditionalJurisdictionalUser = {
  ministryName: string;
  organizationName: string;
  businessManagerNames: string;
}[];

// 型定義（段階的改善用）

/**
 * プロジェクト帳票用のデータ取得フック
 * シートタイプ（RS/SS/KS）に応じて必要なAPIのみを実行し、パフォーマンスを最適化
 */
export type ProjectLedgerData = {
  isLoading: boolean;
  hasAllRequiredData: boolean;
  sheetType: SheetType | undefined;
  // 基本データ（将来的に型改善予定）
  project: z.infer<typeof ProjectModel> | undefined;
  ministries: z.infer<typeof MinistriesModel> | undefined;
  ministryName: string | undefined;
  organizationNameBySnapshot: string | undefined;
  otherOrganizationNameBySnapshot: string | undefined;
  businessManagerNameBySnapshot: string | undefined;
  relatedMaterials: z.infer<typeof RelatedMaterialsModel> | undefined;
  paymentGroups: z.infer<typeof PaymentGroupsModel> | undefined;
  paymentGraph: z.infer<typeof PaymentGraphModel> | undefined;
  payments: z.infer<typeof PaymentsModel> | undefined;
  activityTreeOption: ReturnType<typeof useActivityTreeOptions>;
  nestedConsecutiveIndicators: z.infer<typeof IndicatorsModel>[] | undefined;
  routeIndicatorIds: string[];
  // シートタイプ別データ（将来的に型改善予定）
  data: {
    rs: {
      accountingTypes: z.infer<typeof AccountingTypesModel> | undefined;
      accountTitleOptions:
        | {
            value: string;
            label: string;
          }[]
        | undefined;
      policies: z.infer<typeof PoliciesWithOrganizationNameModel> | undefined;
      consecutiveFiscalYears:
        | z.infer<typeof ConsecutiveFiscalYearsModel>
        | undefined;
      consecutiveRequestAmounts:
        | z.infer<typeof ConsecutiveRequestAmountsModel>
        | undefined;
      consecutiveBudgets: z.infer<typeof ConsecutiveBudgetsModel> | undefined;
      consecutiveExecutions:
        | z.infer<typeof ConsecutiveExecutionsModel>
        | undefined;
    };
    ss: {
      consecutiveFiscalYears:
        | z.infer<typeof ConsecutiveFiscalYearsModel>
        | undefined;
      independentAdministrativeAgencyName: string | undefined;
      segmentExpenditureAndExecution:
        | z.infer<typeof SegmentExpenditureAndExecutionsModel>
        | undefined;
    };
    ks: {
      additionalJurisdictionalUsersBySnapshot:
        | AdditionalJurisdictionalUser
        | undefined;
      fundBusinesses: z.infer<typeof FundBusinessesModel> | undefined;
    };
  };
};

export const useProjectLedgerData = (
  projectId: string,
  onDataReady?: () => void,
): ProjectLedgerData | null => {
  // onDataReadyをrefで保持して依存関係を安定化
  const onDataReadyRef = useRef(onDataReady);
  onDataReadyRef.current = onDataReady;

  // 基本データ（全シート共通）
  const project = useProject(projectId);
  const ministries = useMinistries();

  const projectOrganizations = useProjectHasRelatedOrganizations({
    projectId,
    params: {
      showMigrated: true,
      historySavedAt: project.data?.historySavedAt,
    },
    enabled: !!projectId && !!project.data,
  });

  const relatedMaterials = useRelatedMaterials(projectId);
  const paymentGroups = usePaymentGroups({ projectId });
  const paymentGraph = usePaymentGraph(projectId);
  const payments = useContractorPayments({ projectId });

  const activityTreeOption = useActivityTreeOptions(projectId);
  const routeIndicatorIds = uniq(
    activityTreeOption.selectOptions.flatMap((option) =>
      option.routeIndicators.flatMap((v) => v.indicator.id),
    ),
  );
  const nestedConsecutiveIndicators =
    useNestedConsecutiveIndicators(routeIndicatorIds);

  // シートタイプを取得
  const sheetType = project.data?.sheetType as SheetType | undefined;

  // RSシート固有のデータ（条件付きで実行）
  const accountingTypes = useAccountingTypes({
    enabled: sheetType === 'RS',
  });
  const accountTitleOptions = useAccountTitleOptions({
    enabled: sheetType === 'RS',
  });
  const policies = usePolicies({ enabled: sheetType === 'RS' });

  // RSとSSシート共通のデータ
  const consecutiveFiscalYears = useConsecutiveFiscalYears({
    id: projectId,
    enabled: sheetType === 'RS' || sheetType === 'SS',
  });

  // RS専用のデータ
  const consecutiveRequestAmounts = useConsecutiveRequestAmounts({
    id: projectId,
    enabled: sheetType === 'RS',
  });
  const consecutiveBudgets = useConsecutiveBudgets({
    id: projectId,
    enabled: sheetType === 'RS',
  });
  const consecutiveExecutions = useConsecutiveExecutions({
    id: projectId,
    enabled: sheetType === 'RS',
  });

  const segmentExpenditureAndExecution = useSegmentExpenditureAndExecution({
    projectId,
    enabled: sheetType === 'SS',
  });

  // KSシート固有のデータ
  const additionalJurisdictionalBusinessManagers = useFilteredUsers({
    params: {
      filterBy: 'ids',
      ids:
        project.data?.fundAdditionalAttributes?.additionalJurisdictionalUsers?.flatMap(
          (v) => v.businessManagerIds,
        ) ?? [],
      historySavedAt: project.data?.historySavedAt,
      showMigrated: true,
    },
    enabled:
      sheetType === 'KS' &&
      !!project.data?.fundAdditionalAttributes?.additionalJurisdictionalUsers
        ?.length,
    select: (data) => data,
  });
  const fundBusinesses = useFundBusinesses({ projectId });

  // 省庁名を取得
  const ministryName = getMinistryName({
    targetId: project.data?.ministryId,
    ministries: ministries.data,
  });

  const additionalJurisdictionalUsers =
    useMemo<AdditionalJurisdictionalUser>(() => {
      const projectData = project.data;
      const additionalJurisdictionalUsers =
        projectData?.fundAdditionalAttributes?.additionalJurisdictionalUsers;
      if (
        !ministries.data ||
        sheetType !== 'KS' ||
        !additionalJurisdictionalUsers?.length ||
        projectData?.fundAdditionalAttributes?.fundSheetType !== 'standard'
      ) {
        return [];
      }

      return (
        additionalJurisdictionalUsers.map((value) => {
          return {
            ministryName:
              ministries.data?.find((v) => v.id === value.ministryId)?.name ??
              '',
            organizationName:
              projectData.additionalJurisdictionalOrganizations
                ?.find((v) => v.id === value.organizationId)
                ?.names.join(ITEM_SPLITTER) ?? '',
            businessManagerNames: value.businessManagerIds
              .flatMap((id) => {
                const name =
                  projectData.additionalJurisdictionalBusinessManagers?.find(
                    (v) => v.id === id,
                  )?.name;
                return name ? [name] : [];
              })
              .join('\n'),
          };
        }) ?? []
      );
    }, [project.data, sheetType, ministries.data]);

  // ローディング状態をシートタイプごとに管理
  const isLoading = useMemo(() => {
    // 共通のローディング
    const commonLoading =
      project.isPending ||
      ministries.isPending ||
      projectOrganizations.isPending ||
      relatedMaterials.isPending ||
      paymentGroups.isPending ||
      payments.isPending ||
      paymentGraph.isPending ||
      activityTreeOption.tree.isPending ||
      (routeIndicatorIds.length > 0 && nestedConsecutiveIndicators.isPending);

    if (commonLoading) {
      return true;
    }

    // シートタイプ別のローディング（関連するもののみチェック）
    switch (sheetType) {
      case 'RS':
        return (
          accountingTypes.isPending ||
          accountTitleOptions.isPending ||
          policies.isPending ||
          consecutiveFiscalYears.isPending ||
          consecutiveRequestAmounts.isPending ||
          consecutiveBudgets.isPending ||
          consecutiveExecutions.isPending
        );
      case 'SS':
        return (
          consecutiveFiscalYears.isPending ||
          segmentExpenditureAndExecution.isPending
        );
      case 'KS':
        return (
          fundBusinesses.isPending ||
          (project.data?.fundAdditionalAttributes?.additionalJurisdictionalUsers
            ?.length
            ? additionalJurisdictionalBusinessManagers.isPending
            : false)
        );
      default:
        return false;
    }
  }, [
    // 共通依存関係
    project.isPending,
    ministries.isPending,
    projectOrganizations.isPending,
    relatedMaterials.isPending,
    paymentGroups.isPending,
    payments.isPending,
    paymentGraph.isPending,
    activityTreeOption.tree.isPending,
    routeIndicatorIds.length,
    nestedConsecutiveIndicators.isPending,
    sheetType,
    // 全ての依存関係（シートタイプに関わらず）
    accountingTypes.isPending,
    accountTitleOptions.isPending,
    policies.isPending,
    consecutiveFiscalYears.isPending,
    consecutiveRequestAmounts.isPending,
    consecutiveBudgets.isPending,
    consecutiveExecutions.isPending,
    segmentExpenditureAndExecution.isPending,
    fundBusinesses.isPending,
    additionalJurisdictionalBusinessManagers.isPending,
    project.data?.fundAdditionalAttributes?.additionalJurisdictionalUsers
      ?.length,
  ]);

  // 必要なデータがすべて揃っているかチェック
  const hasAllRequiredData = useMemo(() => {
    // 共通の必須データ
    if (!project.data || !ministries.data) {
      return false;
    }

    // activityTreeのローディングが完了していることを確認（データがなくてもOK）
    if (activityTreeOption.tree.isPending) {
      return false;
    }

    // シートタイプ別の必須データ
    switch (sheetType) {
      case 'RS':
        return !!(
          accountingTypes.data &&
          accountTitleOptions.data &&
          policies.data
        );
      case 'SS':
        return true; // SSは追加の必須データなし
      case 'KS':
        return true; // KSは追加の必須データなし
      default:
        return false;
    }
  }, [
    project.data,
    ministries.data,
    accountingTypes.data,
    accountTitleOptions.data,
    policies.data,
    activityTreeOption.tree.isPending,
    sheetType,
  ]);

  // データ準備完了時にコールバックを実行
  useEffect(() => {
    if (!isLoading && hasAllRequiredData && onDataReadyRef.current) {
      onDataReadyRef.current();
    }
  }, [isLoading, hasAllRequiredData]);

  return {
    // 状態
    isLoading,
    hasAllRequiredData,
    sheetType,

    // 共通データ
    project: project.data,
    ministries: ministries.data,
    ministryName,
    organizationNameBySnapshot:
      project.data?.organization.names.join(ITEM_SPLITTER),
    businessManagerNameBySnapshot: project.data?.businessManagers
      .map((person) => person.name)
      .join('\n'),
    otherOrganizationNameBySnapshot: project.data?.otherResponsibleOrganizations
      .map((organization) => organization.names.join(ITEM_SPLITTER))
      .join('\n'),
    relatedMaterials: relatedMaterials.data,
    paymentGroups: paymentGroups.data,
    paymentGraph: paymentGraph.data,
    payments: payments.data,
    activityTreeOption,
    nestedConsecutiveIndicators: nestedConsecutiveIndicators.data,
    routeIndicatorIds,

    // シートタイプ別データ（必要なもののみ）
    data: {
      // RSシート固有（RSシートの時のみ有効なデータ）
      rs:
        sheetType === 'RS'
          ? {
              accountingTypes: accountingTypes.data,
              accountTitleOptions: accountTitleOptions.data,
              policies: policies.data,
              consecutiveFiscalYears: consecutiveFiscalYears.data,
              consecutiveRequestAmounts: consecutiveRequestAmounts.data,
              consecutiveBudgets: consecutiveBudgets.data,
              consecutiveExecutions: consecutiveExecutions.data,
            }
          : {
              accountingTypes: undefined,
              accountTitleOptions: undefined,
              policies: undefined,
              consecutiveFiscalYears: undefined,
              consecutiveRequestAmounts: undefined,
              consecutiveBudgets: undefined,
              consecutiveExecutions: undefined,
            },
      // SSシート固有（SSシートの時のみ有効なデータ）
      ss:
        sheetType === 'SS'
          ? {
              independentAdministrativeAgencyName:
                project.data?.independentAdministrativeAgency?.name,
              segmentExpenditureAndExecution:
                segmentExpenditureAndExecution.data,
              consecutiveFiscalYears: consecutiveFiscalYears.data,
            }
          : {
              independentAdministrativeAgencyName: undefined,
              segmentExpenditureAndExecution: undefined,
              consecutiveFiscalYears: undefined,
            },
      // KSシート固有（KSシートの時のみ有効なデータ）
      ks:
        sheetType === 'KS'
          ? {
              additionalJurisdictionalUsersBySnapshot:
                additionalJurisdictionalUsers,
              fundBusinesses: fundBusinesses.data,
            }
          : {
              additionalJurisdictionalUsersBySnapshot: undefined,
              fundBusinesses: undefined,
            },
    },
  };
};
